import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from './api';

const AuthContext = createContext(null);

// Inactivity timeout in milliseconds (30 minutes)
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const inactivityTimerRef = useRef(null);

  const resetInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    if (isAuthenticated) {
      inactivityTimerRef.current = setTimeout(async () => {
        try {
          await api.post('/logout');
          localStorage.removeItem('user');
          setUser(null);
          setIsAuthenticated(false);
          window.location.href = '/';
        } catch (error) {
          console.error('Auto-logout error:', error);
        }
      }, INACTIVITY_TIMEOUT);
    }
  };

  useEffect(() => {
    // Check for existing user data on mount
    const storedUser = localStorage.getItem('user');
    if (storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser && typeof parsedUser === 'object') {
          setUser(parsedUser);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('user'); // Clean up invalid data
      }
    }
    setIsLoading(false);

    // Set up activity listeners
    const activityEvents = [
      'mousedown', 'mousemove', 'keypress',
      'scroll', 'touchstart', 'click'
    ];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    // Add activity listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Initial timer setup
    resetInactivityTimer();

    // Cleanup
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated]);

  const login = async (credentials) => {
    try {
      const response = await api.post('/login', credentials);
      console.log('Login response:', response); // Debug log

      if (!response || !response.data) {
        throw new Error('No response received from server');
      }

      const { user } = response.data;
      
      if (!user || typeof user !== 'object') {
        throw new Error('Invalid user data received from server');
      }

      // Store user data
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      setIsAuthenticated(true);

      // Reset inactivity timer after login
      resetInactivityTimer();
      
      return user;
    } catch (error) {
      console.error('Login error:', error);
      // Clear any partial data
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
      
      // Throw a more descriptive error
      throw new Error(
        error.response?.data?.error || 
        error.message || 
        'Login failed. Please check your credentials and try again.'
      );
    }
  };

  const logout = async () => {
    try {
      // Call the logout endpoint
      await api.post('/logout');
      
      // Clear local storage
      localStorage.removeItem('user');
      
      // Reset auth state
      setUser(null);
      setIsAuthenticated(false);
      
      // Clear any cached data
      sessionStorage.clear();
      
      // Clear inactivity timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      
      // Force reload to clear any cached state
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      // Even if the API call fails, clear local state
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
      window.location.href = '/';
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 